import { MDSTextColor, MDSTextWeight } from "@/mds";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useCallback } from "react";
import { useLocation } from "react-router";
import { windowModule } from "@/modules/window";
import { MDSText, MDSTextVariant } from "@/mds";
import { MDSLoadingIcon, MDSLoadingIconSize } from "@/mds/components/loading/loading-icon";
import {
  resolveCurrentGoogleAnalyticsClientIdFromBrowserCookie,
  resolveCurrentGoogleAnalyticsSessionIdFromBrowserCookie,
} from "@/modules/metrics/google-analytics.utils";
import { resolveSegmentAnonymousId } from "@/modules/metrics/segment.utils";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

const SEGMENT_ANON_ID_QUERY_PARAM = "segment_anonymous_id";
const GA_CLIENT_ID_QUERY_PARAM = "ga_client_id";
const GA_SESSION_ID_QUERY_PARAM = "ga_session_id";

export const GoogleOAuthRedirectDesktopPage: FC = observer(() => {
  const location = useLocation();

  const resolveBrowserMetadata = useCallback(async (): Promise<{
    segmentAnonymousId: string | null;
    gaClientId: string | null;
    gaSessionId: string | null;
  }> => {
    logger.info({
      message:
        "[GoogleOAuthRedirectDesktopPage][resolveBrowserMetadata] Resolving browser metadata.",
    });

    const [segmentAnonymousId, gaClientId, gaSessionId] = await Promise.all([
      resolveSegmentAnonymousId().catch(() => null),
      resolveCurrentGoogleAnalyticsClientIdFromBrowserCookie().catch(() => null),
      resolveCurrentGoogleAnalyticsSessionIdFromBrowserCookie().catch(() => null),
    ]);

    logger.info({
      message:
        "[GoogleOAuthRedirectDesktopPage][resolveBrowserMetadata] Browser metadata resolved.",
      info: objectModule.safeAsJson({ segmentAnonymousId, gaClientId, gaSessionId }),
    });

    return {
      segmentAnonymousId: segmentAnonymousId ?? null,
      gaClientId: gaClientId ?? null,
      gaSessionId: gaSessionId ?? null,
    };
  }, []);

  const navigateToTargetUrl = useCallback(async () => {
    const targetSearchParams = new URLSearchParams(location.search);
    const metadata = await resolveBrowserMetadata();

    if (metadata.segmentAnonymousId) {
      targetSearchParams.set(SEGMENT_ANON_ID_QUERY_PARAM, metadata.segmentAnonymousId);
    }

    if (metadata.gaClientId) {
      targetSearchParams.set(GA_CLIENT_ID_QUERY_PARAM, metadata.gaClientId);
    }

    if (metadata.gaSessionId) {
      targetSearchParams.set(GA_SESSION_ID_QUERY_PARAM, metadata.gaSessionId);
    }

    const serializedParams = targetSearchParams.toString();

    const targetUrl = new URL(
      serializedParams.length > 0
        ? `mem://google-oauth-redirect?${serializedParams}`
        : `mem://google-oauth-redirect`
    );

    await windowModule.navigateToExternal({
      url: targetUrl,
    });
  }, [location.search, resolveBrowserMetadata]);

  useEffectOnMount(() => {
    void navigateToTargetUrl();
  });

  return (
    <Wrapper>
      <MDSLoadingIcon size={MDSLoadingIconSize.Large} />
      <LoadingMessage
        variant={MDSTextVariant.HeaderM}
        weight={MDSTextWeight.Regular}
        color={MDSTextColor.Contrast}
      >
        Opening Mem...
      </LoadingMessage>
      <MDSText
        variant={MDSTextVariant.TextS}
        weight={MDSTextWeight.Regular}
        color={MDSTextColor.Secondary}
      >
        Click to <StyledLink onClick={() => void navigateToTargetUrl()}>open</StyledLink> if nothing
        happens
      </MDSText>
    </Wrapper>
  );
});

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  flex-wrap: nowrap;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
`;

const LoadingMessage = styled(MDSText)`
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const StyledLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;
